// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { InformationCircleIcon, ArrowPathIcon, BoltIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import CardContainer from '../moduleComponents/CardContainer';
import {
  getPluggyApiToken,
  getPluggyCredentials,
  getPluggyItems,
  getPluggyAccounts,
  syncPluggyAccounts,
} from 'views/finance/moduleServices/pluggy_services';
import CardPluggyConnection from './viewComponents/card_pluggy_connection';
import CardPluggyAccounts from './viewComponents/card_pluggy_accounts';
import CardPluggyTransactions from './viewComponents/card_pluggy_transactions';

// ###### MAIN COMPONENT
const TabFinanceExtras = () => {
  // ###### STATES
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState({
    credentials: false,
    items: false,
    accounts: false,
    transactions: false,
    token: false
  });

  // ###### EFFECTS
  useEffect(() => {
    const fetchPluggyInitialData = async () => {
      setLoadError('');
      try {
        setLoading(prev => ({ ...prev, credentials: true, items: true }));
        const creds = await getPluggyCredentials();
        setClientId(creds.client_id);
        setClientSecret(creds.client_secret);
        setLoading(prev => ({ ...prev, credentials: false }));

        const res = await getPluggyItems();
        setItems(res || []);
        setLoading(prev => ({ ...prev, items: false }));
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          err.message ||
          'Failed to load Pluggy credentials or items';
        setLoadError(msg);
        setClientId('');
        setClientSecret('');
        setItems([]);
        toast.error(msg);
        console.error('Error fetching Pluggy credentials or items:', err);
        setLoading(prev => ({ ...prev, credentials: false, items: false }));
      }
    };

    fetchPluggyInitialData();
  }, []);

  return (
    <div className="flex flex-col gap-2 w-full mt-2">
      {/* Card Open Finance Instructions */}
      <CardContainer title="Open Finance" collapsible={true} defaultExpanded={true}>
        <div className="space-y-4 text-sm text-gray-700">
          <p className="font-semibold">
            Open Finance is a system that allows you to securely share your financial data with apps
          </p>

          <div className="space-y-4">

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-800 flex items-center justify-center mt-0.5">
                <InformationCircleIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">What is it</h3>
                <p className="text-sm text-gray-600">
                  A system of Brazil Central Bank that allows users to securely share their financial data with apps via official APIs.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-800 flex items-center justify-center mt-0.5">
                <ArrowPathIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">How it works</h3>
                <p className="text-sm text-gray-600">
                  Users authorize access and providers connect to banks, collecting data such as balances, transactions, and cards.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-800 flex items-center justify-center mt-0.5">
                <BoltIcon className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 mb-1">Providers</h3>
                <p className="text-sm text-gray-600">
                  Pluggy - Partner that facilitates this connection quickly and simply, ensuring a practical experience.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Belvo - Partner that offers a more complete and detailed connection, ideal for those who want more depth in information.
                </p>
              </div>
            </div>


          </div>
        </div>
      </CardContainer>

      {loadError ? (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-4">
          <p className="font-semibold">Pluggy connection unavailable</p>
          <p className="mt-1">{loadError}</p>
        </div>
      ) : null}

      <CardPluggyConnection
        clientId={clientId}
        clientSecret={clientSecret}
        items={items}
        loading={loading}
        setLoading={setLoading}
        getPluggyApiToken={getPluggyApiToken}
      />
      <CardPluggyAccounts
        items={items}
        selectedItemId={selectedItemId}
        setSelectedItemId={setSelectedItemId}
        accounts={accounts}
        setAccounts={setAccounts}
        loading={loading}
        setLoading={setLoading}
        getPluggyAccounts={getPluggyAccounts}
        syncPluggyAccounts={syncPluggyAccounts}
      />
      <CardPluggyTransactions
        accounts={accounts}
        selectedAccountId={selectedAccountId}
        setSelectedAccountId={setSelectedAccountId}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default TabFinanceExtras;
